import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Plus, Check, Edit2, Trash2, ChevronDown, ChevronRight, ChevronLeft, X, RotateCcw, Upload, FileText, Image as ImageIcon, Paperclip, Download, ExternalLink, RefreshCw, Calendar, Tag, AlertTriangle } from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { gestaoService } from '../../services/gestaoService'
import leiloesService from '../../services/leiloesService'
import toast from 'react-hot-toast'

const fmt = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const fmtDate = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR') : '—')

// ── Date helpers ──────────────────────────────────────────────────────────────
const isoToDisplay = (iso) => {
  if (!iso) return ''
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}
const isoToDate = (iso) => {
  if (!iso) return null
  const [y, m, d] = iso.split('-')
  return new Date(Number(y), Number(m) - 1, Number(d))
}
const dateToISO = (date) => {
  if (!date) return ''
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r }

// ── DateRangePicker ───────────────────────────────────────────────────────────
const MONTHS_PT = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro']
const DAYS_PT   = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb']

function DateRangePicker({ dataInicio, dataFim, onChange }) {
  const today = new Date(); today.setHours(0, 0, 0, 0)

  const [open, setOpen]           = useState(false)
  const [pos, setPos]             = useState({ top: 0, left: 0 })
  const [tempStart, setTempStart] = useState(null)
  const [tempEnd, setTempEnd]     = useState(null)
  const [selecting, setSelecting] = useState('start')
  const [hoverDate, setHoverDate] = useState(null)
  const [months, setMonths]       = useState(() => {
    const m = today.getMonth(), y = today.getFullYear()
    return {
      left:  { year: m === 0 ? y - 1 : y, month: m === 0 ? 11 : m - 1 },
      right: { year: y, month: m },
    }
  })
  const triggerRef  = useRef(null)
  const popoverRef  = useRef(null)

  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        popoverRef.current && !popoverRef.current.contains(e.target)
      ) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const openPicker = () => {
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect()
      setPos({ top: r.bottom + 6, left: r.left })
    }
    setTempStart(isoToDate(dataInicio))
    setTempEnd(isoToDate(dataFim))
    setSelecting('start')
    setOpen(true)
  }

  const shiftMonths = (delta) => setMonths(({ left, right }) => {
    const shift = ({ year, month }) => {
      const m = month + delta
      if (m < 0)  return { year: year - 1, month: 11 }
      if (m > 11) return { year: year + 1, month: 0 }
      return { year, month: m }
    }
    return { left: shift(left), right: shift(right) }
  })

  const handleDayClick = (date) => {
    if (selecting === 'start' || !tempStart || date < tempStart) {
      setTempStart(date); setTempEnd(null); setSelecting('end')
    } else {
      setTempEnd(date); setSelecting('start')
    }
  }

  const applyPreset = (start, end) => { setTempStart(start); setTempEnd(end); setSelecting('start') }

  const handleApply = () => {
    if (!tempStart) return
    onChange(dateToISO(tempStart), dateToISO(tempEnd || tempStart))
    setOpen(false)
  }

  const presets = [
    { label: 'Hoje',          fn: () => applyPreset(today, today) },
    { label: 'Ontem',         fn: () => { const d = addDays(today, -1); applyPreset(d, d) } },
    { label: 'Últimos 7 dias',  fn: () => applyPreset(addDays(today, -6), today) },
    { label: 'Últimos 30 dias', fn: () => applyPreset(addDays(today, -29), today) },
    { label: 'Este mês',      fn: () => applyPreset(new Date(today.getFullYear(), today.getMonth(), 1), today) },
    { label: 'Mês passado',   fn: () => applyPreset(
        new Date(today.getFullYear(), today.getMonth() - 1, 1),
        new Date(today.getFullYear(), today.getMonth(), 0)
      )},
    { label: 'Este ano',      fn: () => applyPreset(new Date(today.getFullYear(), 0, 1), today) },
    { label: 'Ano passado',   fn: () => applyPreset(
        new Date(today.getFullYear() - 1, 0, 1),
        new Date(today.getFullYear() - 1, 11, 31)
      )},
  ]

  const renderMonth = (year, month) => {
    const firstDow    = new Date(year, month, 1).getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const cells = []
    for (let i = 0; i < firstDow; i++) cells.push(null)
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d))
    const effectiveEnd = tempEnd || (selecting === 'end' && hoverDate ? hoverDate : null)

    return (
      <div className="w-52">
        <div className="grid grid-cols-7 mb-1">
          {DAYS_PT.map(d => <div key={d} className="text-center text-[11px] text-gray-400 font-medium pb-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((date, i) => {
            if (!date) return <div key={`e${i}`} />
            const iso    = dateToISO(date)
            const isStart = tempStart && dateToISO(tempStart) === iso
            const isEnd   = tempEnd   && dateToISO(tempEnd)   === iso
            const inRange = tempStart && effectiveEnd && date > tempStart && date < effectiveEnd
            const isToday = iso === dateToISO(today)
            return (
              <div key={iso}
                onClick={() => handleDayClick(date)}
                onMouseEnter={() => selecting === 'end' && setHoverDate(date)}
                className={[
                  'text-center text-sm py-[5px] cursor-pointer select-none transition-colors',
                  isStart || isEnd ? 'bg-indigo-600 text-white rounded-full font-semibold' :
                  inRange          ? 'bg-indigo-100 text-indigo-800' :
                  isToday          ? 'text-indigo-600 font-semibold hover:bg-gray-100 rounded-full' :
                                     'text-gray-700 hover:bg-gray-100 rounded-full',
                ].join(' ')}>
                {date.getDate()}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const displayText = dataInicio
    ? `${isoToDisplay(dataInicio)} – ${isoToDisplay(dataFim || dataInicio)}`
    : 'Selecionar período'

  return (
    <div>
      <button ref={triggerRef} onClick={openPicker}
        className={`flex items-center gap-2 border rounded-lg px-3 py-2 text-sm bg-white transition-colors whitespace-nowrap ${open ? 'border-indigo-400 ring-1 ring-indigo-200' : 'border-gray-200 hover:border-gray-300'}`}>
        <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <span className={dataInicio ? 'text-gray-800' : 'text-gray-400'}>{displayText}</span>
      </button>

      {open && (
        <div ref={popoverRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
          className="bg-white rounded-xl shadow-2xl border border-gray-100 p-5 flex gap-0">
          {/* Presets */}
          <div className="flex flex-col gap-0.5 pr-4 border-r border-gray-100 mr-5 min-w-[130px]">
            {presets.map(p => (
              <button key={p.label} onClick={p.fn}
                className="text-left text-sm px-3 py-2 rounded-lg hover:bg-indigo-50 hover:text-indigo-700 text-gray-600 transition-colors whitespace-nowrap">
                {p.label}
              </button>
            ))}
          </div>

          {/* Calendars */}
          <div className="flex flex-col">
            {/* Month nav header */}
            <div className="flex items-center justify-between mb-3">
              <button onClick={() => shiftMonths(-1)} className="p-1 hover:bg-gray-100 rounded text-gray-500">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="flex gap-8">
                <span className="text-sm font-semibold text-gray-800 w-48 text-center">
                  {MONTHS_PT[months.left.month]} {months.left.year}
                </span>
                <span className="text-sm font-semibold text-gray-800 w-48 text-center">
                  {MONTHS_PT[months.right.month]} {months.right.year}
                </span>
              </div>
              <button onClick={() => shiftMonths(1)} className="p-1 hover:bg-gray-100 rounded text-gray-500">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <div className="flex gap-5" onMouseLeave={() => setHoverDate(null)}>
              {renderMonth(months.left.year, months.left.month)}
              <div className="w-px bg-gray-100 self-stretch" />
              {renderMonth(months.right.year, months.right.month)}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
              <span className="text-sm text-gray-500">
                {tempStart
                  ? `${isoToDisplay(dateToISO(tempStart))} – ${tempEnd ? isoToDisplay(dateToISO(tempEnd)) : '...'}`
                  : 'Selecione o período'}
              </span>
              <div className="flex gap-2">
                <button onClick={() => setOpen(false)}
                  className="px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
                  Cancelar
                </button>
                <button onClick={handleApply} disabled={!tempStart}
                  className="px-4 py-1.5 text-sm text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-40 font-medium">
                  Aplicar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const STATUS_LABEL = {
  em_aberto: { label: 'Em Aberto', cls: 'bg-yellow-100 text-yellow-700' },
  atrasado:  { label: 'Atrasado',  cls: 'bg-red-100 text-red-700' },
  pago:      { label: 'Pago',      cls: 'bg-green-100 text-green-700' },
}

const TIPO_LABEL = {
  comissao: { label: 'Comissão', cls: 'bg-green-500 text-white' },
  manual:   { label: 'Manual',   cls: 'bg-gray-400 text-white' },
}

// ── Upload de Comprovantes ────────────────────────────────────────────────────
function FileUploadArea({ files, onChange }) {
  const inputRef = useRef(null)
  useEffect(() => {
    const handlePaste = (e) => {
      const items = Array.from(e.clipboardData?.items || [])
      const imageItems = items.filter(i => i.type.startsWith('image/'))
      if (!imageItems.length) return
      const newFiles = imageItems.map((item, idx) => {
        const blob = item.getAsFile()
        return new File([blob], `colado-${Date.now()}-${idx}.png`, { type: 'image/png' })
      })
      onChange(prev => [...prev, ...newFiles])
    }
    document.addEventListener('paste', handlePaste)
    return () => document.removeEventListener('paste', handlePaste)
  }, [onChange])
  const handleDrop = (e) => {
    e.preventDefault()
    const added = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/') || f.type === 'application/pdf')
    if (added.length) onChange(prev => [...prev, ...added])
  }
  const handleInput = (e) => {
    const added = Array.from(e.target.files || [])
    if (added.length) onChange(prev => [...prev, ...added])
    e.target.value = ''
  }
  const remove = (i) => onChange(prev => prev.filter((_, idx) => idx !== i))
  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-gray-600">Comprovantes (opcional)</label>
      <div onDrop={handleDrop} onDragOver={e => e.preventDefault()} onClick={() => inputRef.current?.click()}
        className="border-2 border-dashed border-gray-200 rounded-lg p-3 text-center cursor-pointer hover:border-blue-300 hover:bg-blue-50/30 transition-colors">
        <Upload className="w-4 h-4 text-gray-300 mx-auto mb-1" />
        <p className="text-xs text-gray-400">Arraste, clique ou <kbd className="font-mono text-[10px] bg-gray-100 border border-gray-200 rounded px-1">Ctrl+V</kbd> para colar</p>
        <p className="text-[11px] text-gray-300 mt-0.5">PDF, PNG, JPG</p>
        <input ref={inputRef} type="file" multiple accept="image/*,application/pdf" onChange={handleInput} className="hidden" />
      </div>
      {files.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-1 bg-gray-100 rounded-lg px-2 py-1 text-xs max-w-[180px]">
              {f.type === 'application/pdf' ? <FileText className="w-3 h-3 text-red-500 flex-shrink-0" /> : <ImageIcon className="w-3 h-3 text-blue-500 flex-shrink-0" />}
              <span className="truncate text-gray-700">{f.name}</span>
              <button onClick={(e) => { e.stopPropagation(); remove(i) }} className="text-gray-400 hover:text-gray-600 flex-shrink-0"><X className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Modal Comprovantes ────────────────────────────────────────────────────────
function ModalComprovantes({ conta, orgId, onClose }) {
  const [comprovantes, setComprovantes] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    gestaoService.listarComprovantesContaReceber(orgId, conta.id)
      .then(r => setComprovantes(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [orgId, conta.id])

  const isImage = (tipo) => tipo && tipo.startsWith('image/')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800">Comprovantes</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-4 space-y-2 max-h-96 overflow-y-auto">
          <p className="text-xs text-gray-500 mb-3">{conta.descricao}</p>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : comprovantes.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">Nenhum comprovante anexado</p>
          ) : (
            comprovantes.map(c => (
              <div key={c.id} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
                {isImage(c.tipo_arquivo)
                  ? <img src={c.url} alt={c.nome_arquivo} className="w-12 h-12 object-cover rounded border border-gray-200 flex-shrink-0" />
                  : <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded border border-red-100 flex-shrink-0">
                      <FileText className="w-6 h-6 text-red-400" />
                    </div>
                }
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-700 truncate">{c.nome_arquivo}</p>
                  {c.tamanho && <p className="text-xs text-gray-400">{(c.tamanho / 1024).toFixed(1)} KB</p>}
                </div>
                <div className="flex gap-1">
                  <a href={c.url} target="_blank" rel="noreferrer"
                    className="p-1.5 text-blue-500 hover:bg-blue-50 rounded" title="Visualizar">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <a href={c.url} download={c.nome_arquivo}
                    className="p-1.5 text-gray-500 hover:bg-gray-100 rounded" title="Baixar">
                    <Download className="w-4 h-4" />
                  </a>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Modal Baixar ──────────────────────────────────────────────────────────────
function ModalBaixar({ conta, formas, onClose, onConfirm }) {
  const saldoDevedor = Number(conta.valor - (conta.valor_recebido || 0))
  const [valorRecebido, setValorRecebido] = useState(saldoDevedor.toFixed(2))
  const [dataBaixa, setDataBaixa] = useState(new Date().toISOString().split('T')[0])
  const [formaPagamentoId, setFormaPagamentoId] = useState(
    formas.find(f => f.is_padrao)?.id || formas[0]?.id || ''
  )
  const [aplicarDesconto, setAplicarDesconto] = useState(false)
  const [desconto, setDesconto] = useState('')
  const [motivoDesconto, setMotivoDesconto] = useState('')
  const [observacoes, setObservacoes] = useState('')
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(false)

  const descontoNum = aplicarDesconto ? (Number(desconto) || 0) : 0
  const valorOriginal = Number(conta.valor)
  const valorRecebidoNum = Number(valorRecebido) || 0
  const totalCoberto = valorRecebidoNum + descontoNum
  const diferenca = valorOriginal - totalCoberto

  // Quando desconto muda → atualiza valor recebido = original - desconto
  const handleDescontoChange = (val) => {
    setDesconto(val)
    const d = Number(val) || 0
    if (d > 0) {
      setValorRecebido(Math.max(0, valorOriginal - d).toFixed(2))
    }
  }

  // Quando ativa desconto, zera valor recebido e aguarda o desconto ser informado
  const handleToggleDesconto = (val) => {
    setAplicarDesconto(val)
    if (!val) {
      setDesconto('')
      setMotivoDesconto('')
      setValorRecebido(saldoDevedor.toFixed(2))
    }
  }

  const handleConfirm = async () => {
    if (!formaPagamentoId) return toast.error('Selecione a forma de pagamento')
    if (aplicarDesconto && descontoNum <= 0) return toast.error('Informe o valor do desconto')
    if (aplicarDesconto && !motivoDesconto.trim()) return toast.error('Informe o motivo do desconto')
    if (descontoNum >= valorOriginal) return toast.error('Desconto não pode ser igual ou maior que o valor total')
    setLoading(true)
    await onConfirm({
      valor_recebido: valorRecebidoNum,
      data_baixa: dataBaixa,
      forma_pagamento_id: Number(formaPagamentoId),
      desconto: descontoNum,
      motivo_desconto: aplicarDesconto ? motivoDesconto.trim() : null,
      observacoes: observacoes.trim() || null,
    }, files)
    setLoading(false)
  }

  const tipo = TIPO_LABEL[conta.tipo_lancamento] || TIPO_LABEL.manual

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-sm sm:text-base font-semibold text-gray-800">Baixar Comissão a Receber</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 ml-2 flex-shrink-0"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-4 sm:px-6 py-4 sm:py-5 space-y-4 overflow-y-auto flex-1">
          {/* Resumo da conta */}
          <div className="bg-gray-50 rounded-lg px-3 py-2 sm:px-4 sm:py-3 space-y-1 sm:space-y-1.5 text-xs sm:text-sm">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-600">Tipo:</span>
              <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${tipo.cls}`}>{tipo.label}</span>
            </div>
            <p className="text-gray-700 line-clamp-2"><span className="font-medium text-gray-600">Desc:</span> {conta.descricao}</p>
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-600">Valor:</span>
              <span className="font-semibold text-gray-800">{fmt(conta.valor)}</span>
            </div>
          </div>

          {/* Valor recebido */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Valor Recebido</label>
            <input
              type="number" step="0.01" value={valorRecebido}
              onChange={e => setValorRecebido(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          {/* Toggle desconto */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => handleToggleDesconto(!aplicarDesconto)}
              className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium transition-colors ${aplicarDesconto ? 'bg-orange-50 text-orange-700' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
            >
              <div className="flex items-center gap-2">
                <Tag className="w-4 h-4" />
                <span>Desconto / Acordo comercial</span>
              </div>
              <div className={`w-10 h-5 rounded-full transition-colors relative ${aplicarDesconto ? 'bg-orange-500' : 'bg-gray-300'}`}>
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${aplicarDesconto ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </div>
            </button>

            {aplicarDesconto && (
              <div className="px-4 py-3 space-y-3 border-t border-orange-100 bg-orange-50/30">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Valor do Desconto</label>
                  <input
                    type="number" step="0.01" value={desconto}
                    onChange={e => handleDescontoChange(e.target.value)}
                    placeholder="0,00"
                    className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Motivo do Desconto <span className="text-red-500">*</span></label>
                  <input
                    type="text" value={motivoDesconto}
                    onChange={e => setMotivoDesconto(e.target.value)}
                    placeholder="Ex: Acordo comercial, negociação, etc."
                    className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Preview do fechamento */}
          {aplicarDesconto && (
            <div className={`rounded-lg px-4 py-3 text-sm space-y-1 ${Math.abs(diferenca) <= 0.01 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex justify-between">
                <span className="text-gray-600">Valor recebido</span>
                <span className="font-medium">{fmt(valorRecebidoNum)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-orange-600">Desconto concedido</span>
                <span className="font-medium text-orange-700">− {fmt(descontoNum)}</span>
              </div>
              <div className="flex justify-between border-t border-gray-200 pt-1 mt-1">
                <span className="font-semibold text-gray-700">Total coberto</span>
                <span className={`font-bold ${Math.abs(diferenca) <= 0.01 ? 'text-green-700' : 'text-red-600'}`}>{fmt(totalCoberto)}</span>
              </div>
              {Math.abs(diferenca) > 0.01 && (
                <p className="text-red-600 text-xs pt-1">Ainda pendente: {fmt(diferenca)}</p>
              )}
            </div>
          )}

          {/* Data e forma de pagamento */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Data de Pagamento</label>
              <input
                type="date" value={dataBaixa}
                onChange={e => setDataBaixa(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Forma de Pagamento</label>
              <select
                value={formaPagamentoId}
                onChange={e => setFormaPagamentoId(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                {formas.filter(f => f.is_ativo).map(f => (
                  <option key={f.id} value={f.id}>{f.nome}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Observações */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Observações (opcional)</label>
            <textarea
              value={observacoes}
              onChange={e => setObservacoes(e.target.value)}
              rows={2}
              placeholder="Ex: Recebido em duas parcelas, combinado com o cliente por telefone..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
            />
          </div>

          <FileUploadArea files={files} onChange={setFiles} />
        </div>
        <div className="flex gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-100 bg-white flex-shrink-0">
          <button onClick={onClose} className="flex-1 px-3 sm:px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            Cancelar
          </button>
          <button
            onClick={handleConfirm} disabled={loading}
            className="flex-1 px-3 sm:px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {loading ? 'Salvando...' : 'Confirmar Baixa'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Modal Nova Conta ──────────────────────────────────────────────────────────
function ModalNovaConta({ leiloes, categorias, onClose, onSalvar }) {
  const [form, setForm] = useState({
    descricao: '', valor: '', data_vencimento: '', leilao_id: '', categoria_id: '',
    tipo_participante: 'comprador', observacoes: '',
  })
  const [loading, setLoading] = useState(false)

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  const handleSalvar = async () => {
    if (!form.descricao || !form.valor || !form.data_vencimento)
      return toast.error('Preencha descrição, valor e data de vencimento')
    setLoading(true)
    await onSalvar({
      ...form,
      valor: Number(form.valor),
      leilao_id: form.leilao_id || null,
      categoria_id: form.categoria_id || null,
    })
    setLoading(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800">Nova Comissão a Receber</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Descrição *</label>
            <input value={form.descricao} onChange={e => set('descricao', e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="Ex: Comissão do leilão..." />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Valor *</label>
            <input type="number" step="0.01" value={form.valor} onChange={e => set('valor', e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="0,00" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Vencimento *</label>
            <input type="date" value={form.data_vencimento} onChange={e => set('data_vencimento', e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Leilão</label>
            <select value={form.leilao_id} onChange={e => set('leilao_id', e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
              <option value="">Nenhum</option>
              {leiloes.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Categoria</label>
            <select value={form.categoria_id} onChange={e => set('categoria_id', e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
              <option value="">Nenhuma</option>
              {categorias.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Observações</label>
            <textarea value={form.observacoes} onChange={e => set('observacoes', e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" rows={2} />
          </div>
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            Cancelar
          </button>
          <button onClick={handleSalvar} disabled={loading}
            className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {loading ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Ações compartilhadas ──────────────────────────────────────────────────────
function ContaAcoes({ conta, onBaixar, onExcluir, onEstornar, onComprovantes }) {
  return (
    <div className="flex items-center gap-1">
      {conta.status !== 'pago' && (
        <button onClick={() => onBaixar(conta)} title="Baixar"
          className="p-1.5 bg-green-500 hover:bg-green-600 text-white rounded-md transition-colors">
          <Check className="w-3.5 h-3.5" />
        </button>
      )}
      {conta.status === 'pago' && (
        <button onClick={() => onEstornar(conta)} title="Estornar"
          className="p-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-md transition-colors">
          <RotateCcw className="w-3.5 h-3.5" />
        </button>
      )}
      {conta.tipo_lancamento !== 'comissao' && (
        <button onClick={() => onExcluir(conta)} title="Excluir"
          className="p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
      {conta.status === 'pago' && (
        <button onClick={onComprovantes} title="Comprovantes"
          className="p-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors">
          <Paperclip className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  )
}

// Compara detalhes_na_baixa vs detalhes atuais e retorna lista de diferenças
function calcDiffLotes(conta) {
  if (!conta.detalhes_na_baixa || !conta.detalhes_na_baixa.length) return []
  const atual = new Map(
    (conta.detalhes || []).filter(d => d.tipo_linha !== 'gta_nota').map(d => [d.lote_id, d])
  )
  return conta.detalhes_na_baixa
    .filter(d => d.tipo_linha !== 'gta_nota')
    .map(d => {
      const curr = atual.get(d.lote_id)
      if (!curr) return { tipo: 'desfaturado', numero_lote: d.numero_lote, valor_na_baixa: d.subtotal }
      if (Math.abs(Number(curr.subtotal) - Number(d.subtotal)) > 0.01)
        return { tipo: 'alterado', numero_lote: d.numero_lote, valor_na_baixa: d.subtotal, valor_atual: curr.subtotal }
      return null
    })
    .filter(Boolean)
}

// ── Card mobile ───────────────────────────────────────────────────────────────
function ContaCard({ conta, orgId, onBaixar, onExcluir, onEstornar }) {
  const [expandido, setExpandido] = useState(false)
  const [modalComprov, setModalComprov] = useState(false)
  const status = STATUS_LABEL[conta.status] || STATUS_LABEL.em_aberto
  const tipo = TIPO_LABEL[conta.tipo_lancamento] || TIPO_LABEL.manual
  const temDetalhes = conta.detalhes?.length > 0 || !!conta.observacoes

  return (
    <div className="border-b border-gray-100 px-4 py-3 last:border-0">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${tipo.cls}`}>{tipo.label}</span>
            <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-semibold ${status.cls}`}>{status.label}</span>
            <span className="text-xs text-gray-400">{fmtDate(conta.data_vencimento)}</span>
          </div>
          <p className="text-sm font-medium text-gray-800 truncate" title={conta.descricao}>{conta.descricao}</p>
          <p className="text-xs text-gray-500 mt-0.5 truncate">{conta.cliente?.nome || '—'} · {conta.leilao?.nome || '—'}</p>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <span className="text-sm font-bold text-gray-800">{fmt(conta.valor)}</span>
          {conta.status === 'pago' && (
            <span className="text-xs text-green-700 font-medium">{fmt(conta.valor_recebido)}</span>
          )}
          {Number(conta.desconto) > 0 && (
            <span className="inline-flex items-center gap-0.5 text-xs text-orange-600 font-medium">
              <Tag className="w-3 h-3" />−{fmt(conta.desconto)}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-2">
          <ContaAcoes conta={conta} onBaixar={onBaixar} onExcluir={onExcluir} onEstornar={onEstornar}
            onComprovantes={() => setModalComprov(true)} />
          {temDetalhes && (
            <button onClick={() => setExpandido(!expandido)}
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
              {expandido ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              Detalhes
            </button>
          )}
        </div>
        {conta.data_baixa && <span className="text-xs text-gray-400">Baixado: {fmtDate(conta.data_baixa)}</span>}
      </div>

      {expandido && conta.observacoes && (
        <div className="mt-2 ml-3 pl-3 border-l-2 border-gray-300 text-xs text-gray-600">
          <span className="font-medium text-gray-500">Observação:</span> {conta.observacoes}
        </div>
      )}

      {expandido && conta.detalhes?.map((d, i) => (
        d.tipo_linha === 'gta_nota' ? (
          <div key={i} className="mt-2 ml-3 pl-3 border-l-2 border-green-200 text-xs text-green-700">
            GTA/Nota: {d.gta > 0 && `GTA ${fmt(d.gta)}`}{d.gta > 0 && d.nota > 0 && ' · '}{d.nota > 0 && `Nota ${fmt(d.nota)}`} = <strong>{fmt(d.gta_nota)}</strong>
          </div>
        ) : d.valor_venda_total === undefined ? (
          <div key={i} className="mt-2 ml-3 pl-3 border-l-2 border-amber-200 text-xs text-gray-600">
            <span className="font-medium">Lote {d.numero_lote}</span>{' '}
            {(d.despesas_itens || []).map((di, j) => `${di.titulo}: ${fmt(di.valor)}`).join(' · ')}
            {' '}<span className="font-semibold text-amber-700">= {fmt(d.subtotal)}</span>
          </div>
        ) : (
          <div key={i} className="mt-2 ml-3 pl-3 border-l-2 border-blue-200 text-xs text-gray-600">
            <span className="font-medium">Lote {d.numero_lote}</span>{' '}
            Venda: {fmt(d.valor_venda_total)} · {d.valor_comissao_defesa !== undefined
              ? `C.Defesa: ${fmt(d.valor_comissao_defesa)}`
              : `C.Compra: ${fmt(d.valor_comissao_compra)} · C.Venda: ${fmt(d.valor_comissao_venda)}`}
            {d.despesas_lote > 0 && ` · Desp: ${fmt(d.despesas_lote)}`}
            {' '}<span className="font-semibold text-blue-700">= {fmt(d.subtotal)}</span>
          </div>
        )
      ))}

      {expandido && conta.status === 'pago' && Number(conta.valor_recebido) > Number(conta.valor) && (() => {
        const diffs = calcDiffLotes(conta)
        return (
          <div className="mt-2 ml-3 pl-3 border-l-2 border-amber-300 text-xs text-amber-700">
            <div className="flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <div className="flex flex-col gap-0.5">
                <span><strong>Crédito de {fmt(Number(conta.valor_recebido) - Number(conta.valor))}:</strong> valor recebido supera o total atual.</span>
                {diffs.map((d, i) => d.tipo === 'desfaturado' ? (
                  <span key={i} className="text-amber-600">Lote {d.numero_lote} desfaturado após baixa — comissão era {fmt(d.valor_na_baixa)}</span>
                ) : (
                  <span key={i} className="text-amber-600">Lote {d.numero_lote} comissão alterada: era {fmt(d.valor_na_baixa)}, agora {fmt(d.valor_atual)} (dif. {fmt(Math.abs(Number(d.valor_na_baixa) - Number(d.valor_atual)))})</span>
                ))}
                {!diffs.length && <span className="text-amber-500">Sincronize para ver o detalhamento.</span>}
              </div>
            </div>
          </div>
        )
      })()}

      {modalComprov && <ModalComprovantes conta={conta} orgId={orgId} onClose={() => setModalComprov(false)} />}
    </div>
  )
}

// ── Linha de tabela (desktop) ─────────────────────────────────────────────────
function ContaRow({ conta, orgId, onBaixar, onExcluir, onEstornar }) {
  const [expandido, setExpandido] = useState(false)
  const [modalComprov, setModalComprov] = useState(false)
  const status = STATUS_LABEL[conta.status] || STATUS_LABEL.em_aberto
  const tipo = TIPO_LABEL[conta.tipo_lancamento] || TIPO_LABEL.manual
  const temDetalhes = conta.detalhes?.length > 0 || !!conta.observacoes

  return (
    <>
      <tr className="hover:bg-gray-50 transition-colors border-b border-gray-100">
        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{fmtDate(conta.data_vencimento)}</td>
        <td className="px-4 py-3">
          <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${tipo.cls}`}>{tipo.label}</span>
        </td>
        <td className="px-4 py-3 text-sm text-gray-700 max-w-[220px]">
          <div className="flex items-center gap-1">
            {temDetalhes && (
              <button onClick={() => setExpandido(!expandido)} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                {expandido ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
            )}
            <span className="truncate" title={conta.descricao}>{conta.descricao}</span>
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-gray-700 max-w-[140px] truncate">{conta.cliente?.nome || '—'}</td>
        <td className="px-4 py-3 text-sm text-gray-500 max-w-[140px] truncate">{conta.leilao?.nome || '—'}</td>
        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{conta.categoria?.nome || '—'}</td>
        <td className="px-4 py-3 text-sm font-semibold text-gray-800 whitespace-nowrap">{fmt(conta.valor)}</td>
        <td className="px-4 py-3 text-sm text-gray-700">
          <div className="flex flex-col gap-0.5">
            <span className="whitespace-nowrap">{fmt(conta.valor_recebido)}</span>
            {conta.data_baixa && (
              <span className="text-xs text-gray-500 whitespace-nowrap">{fmtDate(conta.data_baixa)}</span>
            )}
            {Number(conta.desconto) > 0 && (
              <span className="inline-flex items-center gap-1 text-xs text-orange-600 font-medium whitespace-nowrap" title={conta.motivo_desconto || 'Desconto concedido'}>
                <Tag className="w-3 h-3" />−{fmt(conta.desconto)}
              </span>
            )}
            {conta.status === 'pago' && Number(conta.valor_recebido) > Number(conta.valor) && (
              <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-semibold whitespace-nowrap" title="Valor recebido supera o total atual dos lotes">
                <AlertTriangle className="w-3 h-3" />
                Crédito {fmt(Number(conta.valor_recebido) - Number(conta.valor))}
              </span>
            )}
          </div>
        </td>
        <td className="px-4 py-3">
          <span className={`inline-block px-2 py-1 rounded-md text-xs font-semibold whitespace-nowrap ${status.cls}`}>{status.label}</span>
        </td>
        <td className="px-4 py-3">
          <ContaAcoes conta={conta} onBaixar={onBaixar} onExcluir={onExcluir} onEstornar={onEstornar}
            onComprovantes={() => setModalComprov(true)} />
        </td>
      </tr>

      {modalComprov && <ModalComprovantes conta={conta} orgId={orgId} onClose={() => setModalComprov(false)} />}

      {expandido && conta.observacoes && (
        <tr className="bg-gray-50 border-b border-gray-200">
          <td colSpan={10} className="px-4 py-2 pl-12 text-xs text-gray-600">
            <span className="font-medium text-gray-500">Observação:</span> {conta.observacoes}
          </td>
        </tr>
      )}

      {expandido && conta.detalhes?.map((d, i) => {
        if (d.tipo_linha === 'gta_nota') {
          return (
            <tr key={i} className="bg-green-50/50 border-b border-green-100">
              <td className="px-4 py-2 pl-12 text-xs text-green-700 font-medium" colSpan={2}>GTA / Nota</td>
              <td className="px-4 py-2 text-xs text-green-700" colSpan={3}>
                {d.gta > 0 && `GTA: ${fmt(d.gta)}`}
                {d.gta > 0 && d.nota > 0 && ' · '}
                {d.nota > 0 && `Nota: ${fmt(d.nota)}`}
              </td>
              <td className="px-4 py-2 text-xs font-semibold text-green-700" colSpan={4}>{fmt(d.gta_nota)}</td>
            </tr>
          )
        }
        if (d.valor_venda_total === undefined) {
          return (
            <tr key={i} className="bg-amber-50/50 border-b border-amber-100">
              <td className="px-4 py-2 pl-12 text-xs text-gray-500" colSpan={2}>Lote {d.numero_lote}</td>
              <td className="px-4 py-2 text-xs text-gray-600" colSpan={3}>
                {(d.despesas_itens || []).map((di, j) => `${di.titulo}: ${fmt(di.valor)}`).join(' · ')}
              </td>
              <td className="px-4 py-2 text-xs font-semibold text-amber-700" colSpan={4}>{fmt(d.subtotal)}</td>
            </tr>
          )
        }
        return (
          <tr key={i} className="bg-blue-50/40 border-b border-blue-100">
            <td className="px-4 py-2 pl-12 text-xs text-gray-500" colSpan={2}>Lote {d.numero_lote}</td>
            <td className="px-4 py-2 text-xs text-gray-600" colSpan={3}>
              Venda: {fmt(d.valor_venda_total)} · {d.valor_comissao_defesa !== undefined
                ? `Com. Defesa: ${fmt(d.valor_comissao_defesa)}`
                : `Com. Compra: ${fmt(d.valor_comissao_compra)} · Com. Venda: ${fmt(d.valor_comissao_venda)}`}
              {d.despesas_itens?.length > 0
                ? d.despesas_itens.map((di, j) => ` · ${di.titulo}: ${fmt(di.valor)}`).join('')
                : d.despesas_lote > 0 ? ` · Despesas: ${fmt(d.despesas_lote)}` : ''}
            </td>
            <td className="px-4 py-2 text-xs font-semibold text-blue-700" colSpan={4}>{fmt(d.subtotal)}</td>
          </tr>
        )
      })}

      {expandido && conta.status === 'pago' && Number(conta.valor_recebido) > Number(conta.valor) && (() => {
        const diffs = calcDiffLotes(conta)
        return (
          <tr className="bg-amber-50 border-b border-amber-200">
            <td colSpan={10} className="px-4 py-2 pl-12 text-xs text-amber-700">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                  <strong>Crédito de {fmt(Number(conta.valor_recebido) - Number(conta.valor))}:</strong>
                  <span>valor recebido supera o total atual dos lotes.</span>
                </div>
                {diffs.map((d, i) => d.tipo === 'desfaturado' ? (
                  <div key={i} className="ml-5 text-amber-600">Lote {d.numero_lote} desfaturado após baixa — comissão era {fmt(d.valor_na_baixa)}</div>
                ) : (
                  <div key={i} className="ml-5 text-amber-600">Lote {d.numero_lote} comissão alterada: era {fmt(d.valor_na_baixa)}, agora {fmt(d.valor_atual)} (dif. {fmt(Math.abs(Number(d.valor_na_baixa) - Number(d.valor_atual)))})</div>
                ))}
                {!diffs.length && <div className="ml-5 text-amber-500">Sincronize para ver o detalhamento.</div>}
              </div>
            </td>
          </tr>
        )
      })()}
    </>
  )
}

// ── Agrupamento por cliente (fluxo cavalo — mesmo cliente pode ter comissão de
// compra, venda, defesa e despesas ao mesmo tempo, ficando difícil de achar
// espalhado em linhas soltas) ──────────────────────────────────────────────
function agruparPorCliente(contas) {
  const grupos = new Map()
  for (const c of contas) {
    const chave = c.cliente?.id ?? c.cliente?.nome ?? `sem-cliente-${c.id}`
    if (!grupos.has(chave)) {
      grupos.set(chave, { chave, cliente: c.cliente, contas: [] })
    }
    grupos.get(chave).contas.push(c)
  }
  return Array.from(grupos.values())
}

function ClienteGrupoCard({ grupo, orgId, onBaixar, onExcluir, onEstornar }) {
  const [aberto, setAberto] = useState(false)
  const totalReceber = grupo.contas.reduce((s, c) => s + Number(c.valor || 0), 0)
  const totalRecebido = grupo.contas.reduce((s, c) => s + Number(c.valor_recebido || 0), 0)
  const temAtrasado = grupo.contas.some(c => c.status === 'atrasado')
  const todosPagos = grupo.contas.every(c => c.status === 'pago')
  const statusGrupo = temAtrasado ? STATUS_LABEL.atrasado : todosPagos ? STATUS_LABEL.pago : STATUS_LABEL.em_aberto

  return (
    <div className="border-b border-gray-100 last:border-0">
      <button onClick={() => setAberto(!aberto)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-gray-50/70 active:bg-gray-100">
        <div className="flex items-center gap-2 min-w-0">
          {aberto ? <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />}
          <div className="min-w-0 text-left">
            <p className="text-sm font-semibold text-gray-800 truncate">{grupo.cliente?.nome || 'Sem cliente'}</p>
            <p className="text-xs text-gray-400">{grupo.contas.length} lançamento{grupo.contas.length > 1 ? 's' : ''}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <span className="text-sm font-bold text-gray-900">{fmt(totalReceber)}</span>
          {totalRecebido > 0 && <span className="text-xs text-green-700 font-medium">{fmt(totalRecebido)} recebido</span>}
          <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-semibold ${statusGrupo.cls}`}>{statusGrupo.label}</span>
        </div>
      </button>
      {aberto && (
        <div className="divide-y divide-gray-100 bg-white">
          {grupo.contas.map(c => (
            <ContaCard key={c.id} conta={c} orgId={orgId} onBaixar={onBaixar} onExcluir={onExcluir} onEstornar={onEstornar} />
          ))}
        </div>
      )}
    </div>
  )
}

function ClienteGrupoRow({ grupo, orgId, onBaixar, onExcluir, onEstornar }) {
  const [aberto, setAberto] = useState(false)
  const totalReceber = grupo.contas.reduce((s, c) => s + Number(c.valor || 0), 0)
  const totalRecebido = grupo.contas.reduce((s, c) => s + Number(c.valor_recebido || 0), 0)
  const temAtrasado = grupo.contas.some(c => c.status === 'atrasado')
  const todosPagos = grupo.contas.every(c => c.status === 'pago')
  const statusGrupo = temAtrasado ? STATUS_LABEL.atrasado : todosPagos ? STATUS_LABEL.pago : STATUS_LABEL.em_aberto

  return (
    <>
      <tr onClick={() => setAberto(!aberto)}
        className="hover:bg-blue-50/60 transition-colors border-b border-gray-200 bg-gray-50/70 cursor-pointer">
        <td className="px-4 py-3" colSpan={3}>
          <div className="flex items-center gap-2">
            {aberto ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
            <span className="font-semibold text-gray-800 text-sm">{grupo.cliente?.nome || 'Sem cliente'}</span>
            <span className="text-xs text-gray-400">
              {grupo.contas.length} lançamento{grupo.contas.length > 1 ? 's' : ''}
            </span>
          </div>
        </td>
        <td /><td /><td />
        <td className="px-4 py-3 text-sm font-bold text-gray-900 whitespace-nowrap">{fmt(totalReceber)}</td>
        <td className="px-4 py-3 text-sm font-semibold text-gray-700 whitespace-nowrap">{fmt(totalRecebido)}</td>
        <td className="px-4 py-3">
          <span className={`inline-block px-2 py-1 rounded-md text-xs font-semibold whitespace-nowrap ${statusGrupo.cls}`}>{statusGrupo.label}</span>
        </td>
        <td />
      </tr>
      {aberto && grupo.contas.map(c => (
        <ContaRow key={c.id} conta={c} orgId={orgId} onBaixar={onBaixar} onExcluir={onExcluir} onEstornar={onEstornar} />
      ))}
    </>
  )
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function ContasReceber() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id
  const isFluxoCavalo = organizadorAtivo?.fluxo_leilao === 'comissao_cavalo'

  const [contas, setContas] = useState([])
  const [leiloes, setLeiloes] = useState([])
  const [formas, setFormas] = useState([])
  const [categorias, setCategorias] = useState([])
  const [loading, setLoading] = useState(false)

  const [modoPeriodo, setModoPeriodo] = useState(false)
  const [leilaoId, setLeilaoId] = useState('')
  const [dataInicio, setDataInicio] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [buscaCliente, setBuscaCliente] = useState('')
  const [statusFiltro, setStatusFiltro] = useState('')
  const [valorMin, setValorMin] = useState('')
  const [valorMax, setValorMax] = useState('')
  const [tipoParticipante, setTipoParticipante] = useState('')
  const [categoriaFiltro, setCategoriaFiltro] = useState('')
  const [ordenacao, setOrdenacao] = useState('vencimento') // 'vencimento' | 'cliente'

  const [modalBaixar, setModalBaixar] = useState(null)
  const [modalNova, setModalNova] = useState(false)
  const [confirmEstorno, setConfirmEstorno] = useState(null)
  const [confirmExcluir, setConfirmExcluir] = useState(null)
  const [syncInicial, setSyncInicial] = useState(false)
  const [syncing, setSyncing] = useState(false)

  // Debounce para filtros instantâneos
  const [debounceTimer, setDebounceTimer] = useState(null)

  const carregarRef = useRef(null)
  const [exportando, setExportando] = useState(false)

  const handleExportarXlsx = async () => {
    setExportando(true)
    try {
      const filtros = {}
      if (!modoPeriodo && leilaoId) filtros.leilao_id = leilaoId
      if (modoPeriodo && dataInicio) filtros.data_inicio = dataInicio
      if (modoPeriodo && dataFim) filtros.data_fim = dataFim
      if (buscaCliente) filtros.busca_cliente = buscaCliente
      if (statusFiltro) filtros.status = statusFiltro
      if (valorMin) filtros.valor_min = Number(valorMin)
      if (valorMax) filtros.valor_max = Number(valorMax)
      if (tipoParticipante) filtros.tipo_participante = tipoParticipante
      if (categoriaFiltro) filtros.categoria_id = categoriaFiltro
      if (ordenacao === 'cliente') filtros.ordenacao = 'cliente'
      const res = await gestaoService.exportarContasReceberXlsx(orgId, filtros)
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }))
      const a = document.createElement('a')
      a.href = url
      a.download = 'comissao_a_receber.xlsx'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Erro ao exportar planilha')
    } finally {
      setExportando(false)
    }
  }

  const carregar = useCallback(async () => {
    if (!orgId) return
    setLoading(true)
    try {
      const filtros = {}
      if (!modoPeriodo && leilaoId) filtros.leilao_id = leilaoId
      if (modoPeriodo && dataInicio) filtros.data_inicio = dataInicio
      if (modoPeriodo && dataFim) filtros.data_fim = dataFim
      if (buscaCliente) filtros.busca_cliente = buscaCliente
      if (statusFiltro) filtros.status = statusFiltro
      if (valorMin) filtros.valor_min = Number(valorMin)
      if (valorMax) filtros.valor_max = Number(valorMax)
      if (tipoParticipante) filtros.tipo_participante = tipoParticipante
      if (categoriaFiltro) filtros.categoria_id = categoriaFiltro
      const res = await gestaoService.listarContasReceber(orgId, filtros)
      setContas(res.data || [])
    } catch {
      toast.error('Erro ao carregar contas')
    } finally {
      setLoading(false)
    }
  }, [orgId, modoPeriodo, leilaoId, dataInicio, dataFim, buscaCliente, statusFiltro, valorMin, valorMax, tipoParticipante, categoriaFiltro])

  useEffect(() => { carregarRef.current = carregar }, [carregar])

  // Auto-filtrar com debounce quando qualquer filtro muda
  useEffect(() => {
    if (!orgId || !syncInicial) return
    if (debounceTimer) clearTimeout(debounceTimer)
    const timer = setTimeout(() => {
      carregar()
    }, 400)
    setDebounceTimer(timer)
    return () => clearTimeout(timer)
  }, [buscaCliente, statusFiltro, valorMin, valorMax, dataInicio, dataFim, modoPeriodo, tipoParticipante, categoriaFiltro])

  useEffect(() => {
    if (!orgId) return
    Promise.all([
      leiloesService.getByOrganizador(orgId),
      gestaoService.listarFormasPagamento(orgId),
      gestaoService.listarCategorias(orgId, 'receita'),
    ]).then(async ([ls, fps, cats]) => {
      const sorted = [...(ls || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
      setLeiloes(sorted)
      setFormas(fps.data || [])
      setCategorias(cats.data || [])
      // Auto-seleciona o leilão mais recente — o segundo effect cuida do sync+carregar
      if (sorted.length && !syncInicial) {
        setSyncInicial(true)
        setLeilaoId(String(sorted[0].id))
      }
    }).catch(() => {})
  }, [orgId])

  // Carrega contas quando leilaoId muda (sincroniza antes)
  useEffect(() => {
    if (!orgId || !leilaoId) return
    setTipoParticipante('')
    const syncAndLoad = async () => {
      try {
        await gestaoService.sincronizar(orgId, leilaoId)
      } catch { /* ignora erro de sync */ }
      carregar()
    }
    syncAndLoad()
  }, [orgId, leilaoId])

  const handleSincronizar = async () => {
    if (!leilaoId) return
    setSyncing(true)
    try {
      const res = await gestaoService.sincronizar(orgId, leilaoId)
      const d = res.data || res
      toast.success(`Sincronizado: ${d.contas_geradas ?? 0} novas, ${d.contas_atualizadas ?? 0} atualizadas`)
      carregar()
    } catch {
      toast.error('Erro ao sincronizar')
    } finally {
      setSyncing(false)
    }
  }

  const handleBaixar = async (dados, files = []) => {
    try {
      await gestaoService.baixarContaReceber(orgId, modalBaixar.id, dados)
      if (files.length) {
        await Promise.allSettled(
          files.map(f => gestaoService.uploadComprovanteContaReceber(orgId, modalBaixar.id, f))
        )
      }
      toast.success('Baixa registrada!')
      setModalBaixar(null)
      carregar()
    } catch {
      toast.error('Erro ao registrar baixa')
    }
  }

  const handleNova = async (dados) => {
    try {
      await gestaoService.criarContaReceber(orgId, dados)
      toast.success('Conta criada!')
      setModalNova(false)
      carregar()
    } catch {
      toast.error('Erro ao criar conta')
    }
  }

  const handleExcluir = (conta) => setConfirmExcluir(conta)

  const confirmarExcluir = async () => {
    try {
      await gestaoService.excluirContaReceber(orgId, confirmExcluir.id)
      toast.success('Conta excluída!')
      setConfirmExcluir(null)
      carregar()
    } catch {
      toast.error('Erro ao excluir')
    }
  }

  const handleEstornar = async (conta) => {
    setConfirmEstorno(conta)
  }

  const confirmarEstorno = async () => {
    const conta = confirmEstorno
    setConfirmEstorno(null)
    try {
      await gestaoService.estornarContaReceber(orgId, conta.id)
      toast.success('Conta estornada!')
      carregar()
    } catch {
      toast.error('Erro ao estornar')
    }
  }

  // Agrupamento por cliente (só fluxo cavalo — mesmo cliente pode ter comissão
  // de compra, venda, defesa e despesas ao mesmo tempo no mesmo leilão)
  const gruposPorCliente = useMemo(() => {
    if (!isFluxoCavalo) return null
    const gs = agruparPorCliente(contas)
    if (ordenacao === 'cliente') {
      gs.sort((a, b) => (a.cliente?.nome || '').localeCompare(b.cliente?.nome || '', 'pt-BR', { sensitivity: 'base' }))
    }
    return gs
  }, [isFluxoCavalo, contas, ordenacao])

  // Lista plana (fluxos não-cavalo) — respeita a ordenação escolhida.
  const contasOrdenadas = useMemo(() => {
    if (isFluxoCavalo) return contas
    if (ordenacao !== 'cliente') return contas
    return [...contas].sort((a, b) => {
      const n = (a.cliente?.nome || '').localeCompare(b.cliente?.nome || '', 'pt-BR', { sensitivity: 'base' })
      return n !== 0 ? n : new Date(a.data_vencimento) - new Date(b.data_vencimento)
    })
  }, [isFluxoCavalo, contas, ordenacao])

  // Totais
  const totais = contas.reduce(
    (acc, c) => {
      acc.total += Number(c.valor)
      acc.recebido += Number(c.valor_recebido || 0)
      acc.desconto += Number(c.desconto || 0)
      if (c.status === 'em_aberto') acc.em_aberto += Number(c.valor)
      if (c.status === 'atrasado') acc.atrasado += Number(c.valor)
      if (c.status === 'pago') acc.pago += Number(c.valor_recebido || 0)
      return acc
    },
    { total: 0, recebido: 0, em_aberto: 0, atrasado: 0, pago: 0, desconto: 0 }
  )

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-bold text-gray-800">Comissão a Receber</h1>
        <div className="flex gap-2">
          {leilaoId && !modoPeriodo && (
            <button onClick={handleSincronizar} disabled={syncing}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Sincronizando...' : 'Sincronizar'}
            </button>
          )}
          <button onClick={handleExportarXlsx} disabled={exportando}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 text-gray-700 rounded-lg text-sm font-medium">
            <Download className="w-4 h-4" />
            {exportando ? 'Exportando...' : 'Exportar Excel'}
          </button>
          <button onClick={() => setModalNova(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium">
            <Plus className="w-4 h-4" />
            Nova Conta
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 space-y-3">
        {/* Linha 1: modo + leilão/período */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg overflow-hidden border border-gray-200 flex-shrink-0">
            <button onClick={() => setModoPeriodo(false)}
              className={`px-3 py-2 text-sm font-medium transition-colors ${!modoPeriodo ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              Por Leilão
            </button>
            <button onClick={() => {
              setModoPeriodo(true)
              if (!dataInicio && !dataFim) {
                const ano = new Date().getFullYear()
                setDataInicio(`${ano}-01-01`)
                setDataFim(`${ano}-12-31`)
              }
              setTimeout(() => carregarRef.current?.(), 0)
            }}
              className={`px-3 py-2 text-sm font-medium transition-colors ${modoPeriodo ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              Por Período
            </button>
          </div>

          {!modoPeriodo ? (
            <select value={leilaoId} onChange={e => setLeilaoId(e.target.value)}
              className="flex-1 min-w-[180px] border border-gray-200 rounded-lg px-3 py-2 text-sm">
              <option value="">Todos os leilões</option>
              {leiloes.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}
            </select>
          ) : (
            <DateRangePicker
              dataInicio={dataInicio}
              dataFim={dataFim}
              onChange={(s, e) => { setDataInicio(s); setDataFim(e) }}
            />
          )}
        </div>

        {/* Linha 2: busca + status + valor */}
        <div className="flex flex-wrap items-center gap-2">
          <input value={buscaCliente} onChange={e => setBuscaCliente(e.target.value)}
            placeholder="Buscar por cliente..."
            className="flex-1 min-w-[160px] border border-gray-200 rounded-lg px-3 py-2 text-sm" />

          <select value={statusFiltro} onChange={e => setStatusFiltro(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm min-w-[130px]">
            <option value="">Todos os status</option>
            <option value="em_aberto">Em Aberto</option>
            <option value="atrasado">Atrasado</option>
            <option value="pago">Pago</option>
          </select>

          <select value={categoriaFiltro} onChange={e => setCategoriaFiltro(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm min-w-[150px]">
            <option value="">Todas as categorias</option>
            {categorias.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>

          <select value={ordenacao} onChange={e => setOrdenacao(e.target.value)}
            title="Ordenar lançamentos"
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm min-w-[150px]">
            <option value="vencimento">Ordenar: Vencimento</option>
            <option value="cliente">Ordenar: Cliente (A-Z)</option>
          </select>

          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-400 whitespace-nowrap">R$</span>
            <input type="number" step="0.01" placeholder="Mín" value={valorMin} onChange={e => setValorMin(e.target.value)}
              className="border border-gray-200 rounded-lg px-2 py-2 text-sm w-20" />
            <span className="text-gray-400 text-sm">–</span>
            <input type="number" step="0.01" placeholder="Máx" value={valorMax} onChange={e => setValorMax(e.target.value)}
              className="border border-gray-200 rounded-lg px-2 py-2 text-sm w-20" />
          </div>

          {leiloes.find(l => String(l.id) === String(leilaoId))?.fluxo_comissao === 'separada' && (
            <div className="flex rounded-lg overflow-hidden border border-gray-200">
              {[
                { value: '',          label: 'Todos' },
                { value: 'comprador', label: 'Comprador' },
                { value: 'vendedor',  label: 'Vendedor' },
              ].map(opt => (
                <button key={opt.value} onClick={() => setTipoParticipante(opt.value)}
                  className={`px-3 py-2 text-sm font-medium transition-colors ${tipoParticipante === opt.value ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          )}

          {(valorMin || valorMax || buscaCliente || statusFiltro || tipoParticipante || categoriaFiltro) && (
            <button onClick={() => { setValorMin(''); setValorMax(''); setBuscaCliente(''); setStatusFiltro(''); setTipoParticipante(''); setCategoriaFiltro('') }}
              className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1 whitespace-nowrap">
              <X className="w-3 h-3" /> Limpar
            </button>
          )}
        </div>
      </div>

      {/* Conteúdo */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : contas.length === 0 ? (
          <p className="px-4 py-12 text-center text-gray-400 text-sm">Nenhuma conta encontrada</p>
        ) : (
          <>
            {/* Cards — mobile */}
            <div className="block md:hidden divide-y divide-gray-100">
              {gruposPorCliente
                ? gruposPorCliente.map(g => (
                    <ClienteGrupoCard key={g.chave} grupo={g} orgId={orgId} onBaixar={setModalBaixar} onExcluir={handleExcluir} onEstornar={handleEstornar} />
                  ))
                : contasOrdenadas.map(c => (
                    <ContaCard key={c.id} conta={c} orgId={orgId} onBaixar={setModalBaixar} onExcluir={handleExcluir} onEstornar={handleEstornar} />
                  ))}
            </div>

            {/* Tabela — desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {['Vencimento','Tipo','Descrição','Cliente','Leilão','Categoria','Valor a Receber','Valor Recebido','Status','Ações'].map(h => (
                      <th key={h} className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {gruposPorCliente
                    ? gruposPorCliente.map(g => (
                        <ClienteGrupoRow key={g.chave} grupo={g} orgId={orgId} onBaixar={setModalBaixar} onExcluir={handleExcluir} onEstornar={handleEstornar} />
                      ))
                    : contasOrdenadas.map(c => (
                        <ContaRow key={c.id} conta={c} orgId={orgId} onBaixar={setModalBaixar} onExcluir={handleExcluir} onEstornar={handleEstornar} />
                      ))}
                </tbody>
              </table>
            </div>

            {/* Totais */}
            <div className="border-t border-gray-200 px-4 py-3 bg-gray-50">
              <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-6 text-sm">
                <span className="text-gray-600">Total: <strong className="text-gray-800">{fmt(totais.total)}</strong></span>
                <span className="text-yellow-700">Em Aberto: <strong>{fmt(totais.em_aberto)}</strong></span>
                <span className="text-red-600">Atrasado: <strong>{fmt(totais.atrasado)}</strong></span>
                <span className="text-green-700">Pago: <strong>{fmt(totais.pago)}</strong></span>
                {totais.desconto > 0 && (
                  <span className="text-orange-600">Descontos: <strong>{fmt(totais.desconto)}</strong></span>
                )}
                <span className="text-gray-500 col-span-2 sm:ml-auto">A receber: <strong className="text-gray-800">{fmt(totais.total - totais.recebido - totais.desconto)}</strong></span>
              </div>
            </div>
          </>
        )}
      </div>

      {modalBaixar && (
        <ModalBaixar conta={modalBaixar} formas={formas}
          onClose={() => setModalBaixar(null)} onConfirm={handleBaixar} />
      )}
      {modalNova && (
        <ModalNovaConta leiloes={leiloes} categorias={categorias}
          onClose={() => setModalNova(false)} onSalvar={handleNova} />
      )}

      {/* Modal Confirmação Exclusão */}
      {confirmExcluir && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            </div>
            <h3 className="font-bold text-gray-900 text-lg mb-2 text-center">Excluir comissão?</h3>
            <p className="text-gray-600 text-sm text-center mb-2">{confirmExcluir.descricao}</p>
            <p className="text-center font-semibold text-lg mb-1">
              {Number(confirmExcluir.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </p>
            <p className="text-xs text-red-600 text-center mb-5">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmExcluir(null)} className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg font-medium text-sm hover:bg-gray-50 transition">
                Cancelar
              </button>
              <button onClick={confirmarExcluir} className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg font-medium text-sm transition">
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmação Estorno */}
      {confirmEstorno && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
            </div>
            <h3 className="font-bold text-gray-900 text-lg mb-2 text-center">Estornar baixa?</h3>
            <p className="text-gray-600 text-sm text-center mb-2">{confirmEstorno.descricao}</p>
            <p className="text-center font-semibold text-lg mb-1">
              {Number(confirmEstorno.valor_recebido || confirmEstorno.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </p>
            <p className="text-xs text-red-600 text-center mb-5">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmEstorno(null)} className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg font-medium text-sm hover:bg-gray-50 transition">
                Cancelar
              </button>
              <button onClick={confirmarEstorno} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white py-2.5 rounded-lg font-medium text-sm transition">
                Estornar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
